import { useCallback, useRef, useState } from 'react'
import type { RefObject } from 'react'

import { getCharsCountForService } from '@buffer-mono/char-toolkit'
import { toast } from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'

import {
  extractSavedReplyText,
  insertText,
} from '../../Comments/CommentReplyForm/utils/replyFormUtils'
import type { InsertedRange } from '../../components/EngagementInsertedTextHighlight'
import { useReplyEmojiInsert } from '../../components/EngagementReplyFormFooter'
import type { SavedReplyItem_SavedReply } from '../../components/SavedRepliesPopover/SavedReplyItem'
import type { Service } from '../../graphqlEnums'

/**
 * Instagram's messaging API rejects text past this length. One value for
 * every DM channel until a channel's own configuration says otherwise.
 */
export const DM_CHARACTER_LIMIT = 1000

type UseMessageDraftParams = {
  service: Service
  textareaRef: RefObject<HTMLTextAreaElement>
}

export type MessageDraft = {
  text: string
  setText: (text: string) => void
  /** The slice a saved reply just added, which the field highlights. */
  insertedRange: InsertedRange | null
  clearInsertedRange: () => void
  insertEmoji: (emoji: string) => void
  insertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void
  savedRepliesRef: RefObject<HTMLDivElement>
  emojiPickerRef: RefObject<HTMLDivElement>
}

/**
 * The message being written: text kept within the network's length limit,
 * and the two insertions the footer offers. No sending yet; the draft lives
 * in memory for as long as the form that owns it.
 */
export function useMessageDraft({
  service,
  textareaRef,
}: UseMessageDraftParams): MessageDraft {
  const [text, setTextState] = useState('')
  const [insertedRange, setInsertedRange] = useState<InsertedRange | null>(null)
  const savedRepliesRef = useRef<HTMLDivElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)

  const setText = useCallback(
    (next: string) => {
      if (getCharsCountForService(next, service) <= DM_CHARACTER_LIMIT) {
        setTextState(next)
      }
    },
    [service],
  )

  const insertEmoji = useReplyEmojiInsert({
    textareaRef,
    replyText: text,
    setReplyText: setText,
    maxChars: DM_CHARACTER_LIMIT,
    channelService: service,
  })

  // A saved reply that would pass the limit is refused with a toast rather
  // than dropped silently; one that fits is appended and highlighted.
  const insertSavedReply = useCallback(
    (savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>) => {
      const savedText = extractSavedReplyText(savedReply)
      const finalText = insertText(text, savedText)
      if (getCharsCountForService(finalText, service) > DM_CHARACTER_LIMIT) {
        toast.error(
          `Cannot insert text. The message would exceed the ${DM_CHARACTER_LIMIT} character limit.`,
          { duration: 3_000 },
        )
        return
      }
      setTextState(finalText)
      // `insertText` always appends, so the saved reply is the tail.
      setInsertedRange({
        start: finalText.length - savedText.length,
        end: finalText.length,
      })
      textareaRef.current?.focus()
    },
    [service, text, textareaRef],
  )

  const clearInsertedRange = useCallback(() => setInsertedRange(null), [])

  return {
    text,
    setText,
    insertedRange,
    clearInsertedRange,
    insertEmoji,
    insertSavedReply,
    savedRepliesRef,
    emojiPickerRef,
  }
}
